import {
  CURRENT_LOCATION_KEY,
  MY_CART,
  NAVIGATION_FROM_APP,
} from '@/constants/common'
import CurrentLocation from '@/models/Location'
import { Cart } from '@/models/Product'

import { getClientName } from '../external'
import {
  getCookieValue,
  getLocalStorageKey,
  isDigiAcreUser,
  isValidJSON,
} from '../helper'
import { conversionLabels } from './conversionLabels'
import CustomEventTypes from './customEventTypes'
import {
  mirrorEcommerceEventToMoengage,
  trackProductEvent,
} from './trackProductEvent'
import {
  CartCaptureEvent,
  ProductListTypeEnum,
  PurchaseEvent,
  ViewEvent,
} from './types'
import { mergeSuperProperties } from './userProperties'
import { mergeSessionUtmParams } from './utmParams'

export const event = (eventType: string, params: Gtag.CustomParams) => {
  if (window.gtag != null) {
    window.gtag(
      'event',
      eventType,
      mergeSessionUtmParams(
        mergeSuperProperties({
          ...params,
          page_location: window.location.href,
        }),
      ),
    )
  }
}

export const logException = (description: string, fatal = false) => {
  if (window.gtag != null) {
    window.gtag('event', 'exception', {
      description,
      fatal,
    })
  }
}

const getListDetails = (type?: ProductListTypeEnum, feedListName = 'feed') => {
  switch (type) {
    case ProductListTypeEnum.SEARCH:
      return { item_list_id: 'product_search', item_list_name: 'Search' }
    case ProductListTypeEnum.PRODUCT:
      return { item_list_id: 'product', item_list_name: 'Product' }
    case ProductListTypeEnum.FEED:
      return { item_list_id: 'feed', item_list_name: feedListName }
    case ProductListTypeEnum.CROP_PROBLEM:
      return { item_list_id: 'crop_problem', item_list_name: 'Crop Problem' }
    case ProductListTypeEnum.PRODUCT_DETAIL:
      return {
        item_list_id: 'product_detail',
        item_list_name: 'Product Detail',
      }
    default:
      return { item_list_id: 'cart', item_list_name: 'Cart' }
  }
}

export const CaptureUpdateCartEvent = ({
  eventType,
  type,
  variantId,
  variantPrice,
  variantDiscount,
  name,
  brandName,
  listName = 'feed',
  quantity = 1,
  index,
  category,
}: CartCaptureEvent) => {
  const cart = getLocalStorageKey<Cart, null>(MY_CART, null)
  const location = getLocalStorageKey<CurrentLocation, null>(
    CURRENT_LOCATION_KEY,
    null,
  )
  const vendorId = cart != null ? cart.vendorId : null
  const price = quantity * variantPrice
  const discount = quantity * variantDiscount
  const totalCost = price - discount
  const gtagParams = mergeSessionUtmParams(
    mergeSuperProperties({
      currency: 'INR',
      value: totalCost,
      items: [
        {
          item_id: variantId,
          item_name: name,
          affiliation: vendorId,
          discount,
          index,
          item_brand: brandName,
          item_category: category?.name,
          ...getListDetails(type, listName),
          item_variant: variantId,
          location_id: location?.placeId,
          price,
          quantity,
        },
      ],
    }),
  )

  if (window.gtag != null) {
    window.gtag('event', eventType, gtagParams)
  }

  mirrorEcommerceEventToMoengage(eventType, gtagParams)
}

export const capturePurchaseEvent = ({
  orderId,
  value,
  currency = 'INR',
  coupon,
  products,
  type = 'purchase',
  shipping,
}: PurchaseEvent) => {
  const gtagParams = mergeSessionUtmParams(
    mergeSuperProperties({
      transaction_id: orderId,
      value,
      currency,
      coupon,
      shipping,
      items: products.map((product, index) => ({
        item_id: product.productId,
        item_name: product.itemName,
        currency,
        discount: product.discount,
        item_brand: product.brandId,
        item_variant: product.variantId,
        item_category: product.categoryId,
        index: index + 1,
        quantity: product.quantity,
        price: product.price,
        affiliation: product.vendorName,
        coupon,
      })),
    }),
  )

  if (window.gtag != null) {
    window.gtag('event', type, gtagParams)
  }

  mirrorEcommerceEventToMoengage(type, gtagParams)
}

export const captureViewEvent = ({
  currency = 'INR',
  type,
  product,
}: ViewEvent) => {
  const { id, name, product_variants: variants, brand, category } = product
  let listType = {}
  if (type != null) {
    listType = getListDetails(type)
  }
  const gtagParams = mergeSessionUtmParams(
    mergeSuperProperties({
      items: variants
        .filter((variant) => variant.inventory_set.length > 0)
        .map((variant, index) => ({
          index,
          currency,
          item_id: id,
          item_name: name,
          discount: variant.inventory_set[0].discount,
          price: variant.inventory_set[0].price,
          item_brand: brand?.name,
          item_category: category?.name,
          item_variant: variant.id,
          ...listType,
        })),
    }),
  )

  if (window.gtag != null) {
    window.gtag('event', 'view_item', gtagParams)
  }

  mirrorEcommerceEventToMoengage('view_item', gtagParams)
}

export const trackCustomEvent = (
  eventType: CustomEventTypes,
  params?: string,
  productName?: string,
) => {
  if (typeof window === 'undefined') return

  let extraParams: Record<string, unknown> = {}
  if (params && isValidJSON(params)) {
    extraParams = { ...JSON.parse(params) }
  }

  let eventParams: Record<string, string> = {
    page_location: window.location?.href ?? '',
    client_src: getClientName(),
    is_ondc: window.location?.pathname?.includes('/ondc') ? '1' : '0',
  }
  let eventName = `${eventType}`
  if (params) {
    eventParams = { ...eventParams, event_params: params }
  }
  if (productName) {
    eventName = `${eventType}/${productName}`
  }

  trackProductEvent(eventName, eventParams, extraParams)
}

export const trackGAConversionEvent = (
  eventCategory: string,
  params: Gtag.CustomParams,
  conversionLabel: conversionLabels,
) => {
  if (window.gtag != null) {
    window.gtag(
      'event',
      'conversion',
      mergeSessionUtmParams(
        mergeSuperProperties({
          send_to: `${process.env.NEXT_PUBLIC_GA_CONVERSION_KEY}/${conversionLabel}`,
          event_category: eventCategory,
          client_src: getClientName(),
          page_location: window.location.href,
          ...params,
        }),
      ),
    )
  }
}
